const SUPPORTED_DICE = new Set([4, 6, 8, 10, 12, 20, 100]);
const DICE_PATTERN = /(\d+)?d(\d+)(kh\d+|kl\d+)?(!)?/gi;
const SIMPLE_DICE_PATTERN = /^(\d+)?d(\d+)$/i;
const MAX_VISUAL_DICE = 500;
const LIVE_ROLL_SETTLE_GRACE_MS = 450;

let diceBox = null;
let diceBoxPromise = null;
let activePlayback = 0;
let activeVisualSession = null;

export const normalizeDiceNotation = (notation) => {
  const normalized = String(notation || '').trim().toLowerCase().replace(/\s+/g, '');
  if (normalized === 'adv' || normalized === 'advantage') return '2d20kh1';
  if (normalized === 'dis' || normalized === 'disadvantage') return '2d20kl1';
  return normalized;
};

export const parseDiceGroups = (notation) => {
  const normalized = normalizeDiceNotation(notation);
  return Array.from(normalized.matchAll(DICE_PATTERN), (match) => ({
    notation: match[0],
    qty: Number(match[1] || 1),
    sides: Number(match[2]),
    exploding: match[4] === '!',
  }));
};

export const buildDiceBoxNotation = (groups) => groups.map(({ sides, qty }) => ({ sides, qty }));

export const combineDiceNotations = (notations) => {
  const normalized = notations.map(normalizeDiceNotation).filter(Boolean);
  const simpleMatches = normalized.map((notation) => notation.match(SIMPLE_DICE_PATTERN));

  if (simpleMatches.length > 0 && simpleMatches.every(Boolean)) {
    const quantitiesBySides = new Map();
    simpleMatches.forEach((match) => {
      const sides = Number(match[2]);
      quantitiesBySides.set(sides, (quantitiesBySides.get(sides) || 0) + Number(match[1] || 1));
    });
    return Array.from(quantitiesBySides, ([sides, qty]) => `${qty}d${sides}`).join('+');
  }

  return normalized.join('+');
};

export const countDiceInNotation = (notation) => parseDiceGroups(notation)
  .reduce((total, group) => total + group.qty, 0);

const createDiceBox = async () => {
  const module = await import('@3d-dice/dice-box');
  const DiceBox = module.default;
  const instance = new DiceBox({
    id: 'wyrdmaker-dice-canvas',
    assetPath: '/assets/dice-box/',
    theme: 'default',
    themeColor: '#7c3aed',
    throwForce: 8,
    spinForce: 7,
    gravity: 1.2,
    scale: 5,
    enableShadows: true,
    shadowTransparency: 0.75,
    lightIntensity: 1.15,
  });

  await instance.init();
  diceBox = instance;
  return instance;
};

const getDiceBox = () => {
  if (!diceBoxPromise) {
    diceBoxPromise = createDiceBox().catch((error) => {
      diceBoxPromise = null;
      diceBox = null;
      throw error;
    });
  }
  return diceBoxPromise;
};

const readValues = (results) => (
  [...results]
    .sort((left, right) => Number(left.rollId) - Number(right.rollId))
    .map((result) => Number(result.value))
);

export const mapDiceBoxResults = (groups, results) => groups.map((group, groupIndex) => {
  const groupResults = results.filter((result) => Number(result.groupId) === groupIndex);
  return readValues(groupResults);
});

const validateGroups = (groups) => groups.length > 0 && !groups.some(({ qty, sides }) => (
  !Number.isInteger(qty)
  || qty < 1
  || qty > 100
  || !SUPPORTED_DICE.has(sides)
));

const finishSessionWithError = (session, error) => {
  if (session.finished) return;
  session.finished = true;
  session.accepting = false;
  window.clearTimeout(session.finishTimer);
  if (activeVisualSession === session) activeVisualSession = null;
  session.reject(error);
};

const finishSessionAsCancelled = (session) => {
  if (!session || session.finished) return;
  session.finished = true;
  session.accepting = false;
  window.clearTimeout(session.finishTimer);
  if (activeVisualSession === session) activeVisualSession = null;
  session.resolve({ cancelled: true });
};

const validateSessionResults = (session) => {
  const invalidResult = session.entries.some((entry) => (
    (entry.group.exploding
      ? entry.values.length < entry.group.qty
      : entry.values.length !== entry.group.qty)
    || entry.values.some((value) => (
      !Number.isInteger(value)
      || value < 1
      || value > entry.group.sides
    ))
  ));

  if (invalidResult) {
    throw new Error('The 3D engine returned an invalid dice result');
  }
};

const getSessionResultGroups = (session) => {
  const allSimple = session.notations.length > 0
    && session.notations.every((notation) => SIMPLE_DICE_PATTERN.test(notation));
  if (!allSimple) {
    return session.entries.map((entry) => ({
      group: entry.group,
      values: entry.values,
    }));
  }

  const groupsBySides = new Map();
  session.entries.forEach((entry) => {
    const sides = entry.group.sides;
    const existing = groupsBySides.get(sides);
    if (existing) {
      existing.group.qty += entry.group.qty;
      existing.group.notation = `${existing.group.qty}d${sides}`;
      existing.values.push(...entry.values);
      return;
    }

    groupsBySides.set(sides, {
      group: {
        ...entry.group,
        notation: `${entry.group.qty}d${sides}`,
      },
      values: [...entry.values],
    });
  });
  return Array.from(groupsBySides.values());
};

const scheduleSessionFinish = (session) => {
  window.clearTimeout(session.finishTimer);
  if (session.finished || session.pendingCollections > 0 || !session.instance) return;

  const revision = session.revision;
  session.finishTimer = window.setTimeout(() => {
    if (
      session.finished
      || session.pendingCollections > 0
      || session.revision !== revision
    ) return;

    try {
      validateSessionResults(session);
      const resultGroups = getSessionResultGroups(session);
      session.finished = true;
      session.accepting = false;
      if (activeVisualSession === session) activeVisualSession = null;
      session.resolve({
        visualRolls: resultGroups.map((entry) => entry.values),
        groups: resultGroups.map((entry) => entry.group),
        notation: combineDiceNotations(session.notations),
      });
    } catch (error) {
      finishSessionWithError(session, error);
    }
  }, LIVE_ROLL_SETTLE_GRACE_MS);
};

const launchGroup = (session, entry) => {
  if (session.finished || entry.launched) return;
  entry.launched = true;
  session.pendingCollections += 1;

  const method = session.hasInitialRoll ? 'add' : 'roll';
  session.hasInitialRoll = true;

  (async () => {
    try {
      const results = await session.instance[method]({
        sides: entry.group.sides,
        qty: entry.group.qty,
      });
      if (session.playbackId !== activePlayback || session.finished) return;

      entry.values = readValues(results);

      if (entry.group.exploding) {
        let pendingDice = entry.values.filter((value) => value === entry.group.sides).length;
        while (pendingDice > 0) {
          if (entry.values.length + pendingDice > MAX_VISUAL_DICE) {
            throw new Error('Too many exploding dice to complete the visual roll');
          }

          const extraResults = await session.instance.add({
            sides: entry.group.sides,
            qty: pendingDice,
          });
          if (session.playbackId !== activePlayback || session.finished) return;

          const extraValues = readValues(extraResults);
          entry.values.push(...extraValues);
          pendingDice = extraValues.filter((value) => value === entry.group.sides).length;
        }
      }
    } catch (error) {
      finishSessionWithError(session, error);
    } finally {
      session.pendingCollections -= 1;
      scheduleSessionFinish(session);
    }
  })();
};

const appendGroupsToSession = (session, notation, groups) => {
  const addedDice = groups.reduce((total, group) => total + group.qty, 0);
  if (session.diceCount + addedDice > MAX_VISUAL_DICE) {
    return { accepted: false, reason: 'too_many_dice' };
  }

  window.clearTimeout(session.finishTimer);
  session.revision += 1;
  session.notations.push(notation);
  session.diceCount += addedDice;

  const entries = groups.map((group) => ({
    group,
    values: [],
    launched: false,
  }));
  session.entries.push(...entries);

  if (session.instance) {
    entries.forEach((entry) => launchGroup(session, entry));
  }

  return {
    accepted: true,
    diceCount: session.diceCount,
    notation: combineDiceNotations(session.notations),
  };
};

/**
 * Adds dice to the physical roll that is currently in flight. The main roll
 * promise remains open until every newly added die has settled.
 */
export const addVisualDiceToRoll = (notation) => {
  const session = activeVisualSession;
  if (!session?.accepting || session.finished) {
    return { accepted: false, reason: 'no_active_roll' };
  }

  const normalized = normalizeDiceNotation(notation);
  const groups = parseDiceGroups(normalized);
  if (!validateGroups(groups)) {
    return { accepted: false, reason: 'unsupported_notation' };
  }

  return appendGroupsToSession(session, normalized, groups);
};

/**
 * Runs the real physics first and returns the values read from the final faces.
 * Those values are then sent to the API and become the input of its calculation.
 */
export const rollVisualDice = async (notation) => {
  const normalized = normalizeDiceNotation(notation);
  const groups = parseDiceGroups(normalized);

  if (!validateGroups(groups)) {
    return { skipped: true, visualRolls: null };
  }

  if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
    return { skipped: true, visualRolls: null };
  }

  finishSessionAsCancelled(activeVisualSession);

  const playbackId = ++activePlayback;
  let resolveSession;
  let rejectSession;
  const completion = new Promise((resolve, reject) => {
    resolveSession = resolve;
    rejectSession = reject;
  });
  const session = {
    playbackId,
    instance: null,
    entries: [],
    notations: [],
    diceCount: 0,
    pendingCollections: 0,
    revision: 0,
    finishTimer: null,
    hasInitialRoll: false,
    accepting: true,
    finished: false,
    resolve: resolveSession,
    reject: rejectSession,
  };
  activeVisualSession = session;
  appendGroupsToSession(session, normalized, groups);

  try {
    const instance = await getDiceBox();
    if (playbackId !== activePlayback || session.finished) {
      finishSessionAsCancelled(session);
      return completion;
    }

    session.instance = instance;
    instance.show();
    session.entries.forEach((entry) => launchGroup(session, entry));
    scheduleSessionFinish(session);
  } catch (error) {
    finishSessionWithError(session, error);
  }

  return completion;
};

export const stopDiceRoll = () => {
  activePlayback += 1;
  finishSessionAsCancelled(activeVisualSession);
  if (!diceBox) return;

  diceBox.clear();
  diceBox.hide('dice-box-canvas--hide');
};

export const fadeDiceRoll = () => {
  diceBox?.hide('dice-box-canvas--hide');
};
